import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Play,
  FileText,
  CheckCircle2,
  Clock,
  Eye,
  Save,
  X,
  MoreVertical,
  Plus,
  Trash2,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Search,
  HelpCircle,
  TrendingUp,
  ArrowUp,
  ArrowDown,
  Loader2,
} from 'lucide-react';
import { AssetSelectionDialog } from '@/components/depreciation/asset-selection-dialog';
import { StaticMetricCardAlt } from '@/components/StaticMetricCardAlt';
import { RevaluationBatchGrid } from '@/components/revaluation/revaluation-batch-grid';
import { ConfirmationModal } from '@/components/ui/confirmation-modal';
import { ComingSoonModal } from '@/components/coming-soon-modal';
import { DemoModeWarningModal } from '@/components/ui/demo-mode-warning-modal';
import { FeatureSettingsGuard } from '@/components/features/FeatureSettingsGuard';
import { ProductTour } from '@/components/ui/product-tour';
import { EvoFeatureFeedbackCollector } from '@/components/FeedbackCollector';
import { useTour } from '@/contexts/TourContext';
import { useRevaluationTour } from '@/hooks/use-revaluation-tour';
import type { Asset } from '@/types/asset';
import type { RevaluationBatch, RevaluationItem, RevaluationMode } from '@/types/revaluation';
import { dataService } from '@/services/data-service';
import { useMode } from '@/providers/ModeProvider';
import { 
  calculateRevaluedNBV, 
  validateRevaluationAmount, 
  getRevaluationAmountPlaceholder,
  recalculateRevaluationItems 
} from '@/lib/revaluation-calculator';
import { mockRevaluationBatches } from '@/lib/mock-revaluation-data';
import { formatCurrency } from '@/lib/format-currency';
import { formatApiErrorMessage } from '@/lib/format-utils';

// Helper function to create default statistics - extracted to reduce cognitive complexity (S3776)
function createDefaultStatistics() {
  return {
    year: new Date().getFullYear(),
    totalRevaluationAdjustment: 125400.00,
    totalAssetsRevalued: 23,
    totalBatches: 4,
    pendingBatchCount: 1,
    pendingAdjustment: 15200.00
  };
}

// Helper function to calculate batch totals - extracted to reduce cognitive complexity (S3776)
function calculateBatchTotals(items: any[]) {
  return {
    totalCurrentNBV: items.reduce((sum: number, item: any) => sum + (item.currentNBV ?? 0), 0),
    totalRevaluedNBV: items.reduce((sum: number, item: any) => sum + (item.revaluedNBV ?? 0), 0),
    totalRevaluationAdjustment: items.reduce((sum: number, item: any) => sum + (item.revaluationAdjustment ?? 0), 0),
  };
}

type SortDirection = 'asc' | 'desc' | null;

interface SortIconProps {
  readonly columnKey: string;
  readonly sortKey: string | null;
  readonly sortDirection: SortDirection;
}

function SortIcon({ columnKey, sortKey, sortDirection }: SortIconProps): React.ReactElement | null {
  if (sortKey !== columnKey) return null;
  return sortDirection === 'asc' ? (
    <ArrowUp className="ml-2 h-4 w-4" />
  ) : (
    <ArrowDown className="ml-2 h-4 w-4" />
  );
}

// Helper function to get sort value for a revaluation batch - extracted to reduce cognitive complexity (S3776)
function getRevaluationBatchSortValue(batch: any, sortKey: string): string | number {
  switch (sortKey) {
    case 'reference':
      return (batch.batchReference ?? batch.id ?? '').toLowerCase();
    case 'date':
      return batch.dateOfRun ? new Date(batch.dateOfRun).getTime() : 0;
    case 'assets':
      return batch.items?.length ?? batch.assets ?? 0;
    case 'adjustment':
      return batch.totalRevaluationAdjustment ?? batch.amount ?? 0;
    case 'postedBy':
      return (batch.postedBy ?? '').toLowerCase();
    case 'postedDate':
      return batch.postedDate ? new Date(batch.postedDate).getTime() : 0;
    case 'status':
      return (batch.status ?? '').toLowerCase();
    default:
      return 0;
  }
}

// Helper function to compare sort values - extracted to reduce cognitive complexity (S3776)
function compareRevaluationSortValues(aValue: string | number, bValue: string | number, direction: 'asc' | 'desc'): number {
  if (aValue === null || aValue === undefined) return 1;
  if (bValue === null || bValue === undefined) return -1;

  if (aValue < bValue) return direction === 'asc' ? -1 : 1;
  if (aValue > bValue) return direction === 'asc' ? 1 : -1;
  return 0;
}

interface TransactionHistoryListProps {
  readonly isLoading: boolean;
  readonly batches: any[];
  readonly searchQuery: string;
  readonly isServerGeneratedId: (id: string) => boolean;
  readonly setCurrentBatch: (batch: any) => void;
  readonly sortConfig: { key: string | null; direction: 'asc' | 'desc' | null };
  readonly onSort: (key: string) => void;
  readonly currentPage: number;
  readonly setCurrentPage: (page: number) => void;
  readonly pageSize: number;
}

function TransactionHistoryList({
  isLoading,
  batches,
  searchQuery,
  isServerGeneratedId,
  setCurrentBatch,
  sortConfig,
  onSort,
  currentPage,
  setCurrentPage,
  pageSize,
}: TransactionHistoryListProps): React.ReactElement {
  const filteredBatches = batches.filter(batch => {
    const query = searchQuery.toLowerCase();
    return (
      (batch.batchReference || batch.id).toLowerCase().includes(query) ||
      batch.status.toLowerCase().includes(query) ||
      (batch.postedBy && batch.postedBy.toLowerCase().includes(query))
    );
  });

  // Apply sorting - using extracted helper functions to reduce cognitive complexity (S3776)
  const sortedBatches = [...filteredBatches].sort((a, b) => {
    if (!sortConfig.key || !sortConfig.direction) return 0;

    const aValue = getRevaluationBatchSortValue(a, sortConfig.key);
    const bValue = getRevaluationBatchSortValue(b, sortConfig.key);
    return compareRevaluationSortValues(aValue, bValue, sortConfig.direction);
  });

  // Pagination calculations
  const totalPages = Math.ceil(sortedBatches.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedBatches = sortedBatches.slice(startIndex, endIndex);

  const handleViewBatch = async (batch: any) => {
    try {
      if (batch.id && isServerGeneratedId(batch.id)) {
        const fullBatch = await dataService.getRevaluationBatch(batch.id);
        const items = fullBatch.items ?? [];
        const formattedBatch = {
          ...fullBatch,
          dateOfRun: fullBatch.dateOfRun?.split('T')[0] || fullBatch.dateOfRun,
          items,
          totalCurrentNBV: fullBatch.totalCurrentNBV ?? items.reduce((sum: number, item: any) => sum + (item.currentNBV ?? 0), 0),
          totalRevaluedNBV: fullBatch.totalRevaluedNBV ?? items.reduce((sum: number, item: any) => sum + (item.revaluedNBV ?? 0), 0),
          totalRevaluationAdjustment: fullBatch.totalRevaluationAdjustment ?? items.reduce((sum: number, item: any) => sum + (item.revaluationAdjustment ?? 0), 0),
        };
        setCurrentBatch(formattedBatch);
      } else {
        const items = batch.items ?? [];
        const formattedBatch = {
          ...batch,
          dateOfRun: batch.dateOfRun?.split('T')[0] || batch.dateOfRun,
          items,
          totalCurrentNBV: batch.totalCurrentNBV ?? items.reduce((sum: number, item: any) => sum + (item.currentNBV ?? 0), 0),
          totalRevaluedNBV: batch.totalRevaluedNBV ?? items.reduce((sum: number, item: any) => sum + (item.revaluedNBV ?? 0), 0),
          totalRevaluationAdjustment: batch.totalRevaluationAdjustment ?? items.reduce((sum: number, item: any) => sum + (item.revaluationAdjustment ?? 0), 0),
        };
        setCurrentBatch(formattedBatch);
      }
    } catch (error) {
      console.error('Error loading batch:', error);
      const items = batch.items ?? [];
      const formattedBatch = {
        ...batch,
        dateOfRun: batch.dateOfRun?.split('T')[0] || batch.dateOfRun,
        items,
        totalCurrentNBV: batch.totalCurrentNBV ?? items.reduce((sum: number, item: any) => sum + (item.currentNBV ?? 0), 0),
        totalRevaluedNBV: batch.totalRevaluedNBV ?? items.reduce((sum: number, item: any) => sum + (item.revaluedNBV ?? 0), 0),
        totalRevaluationAdjustment: batch.totalRevaluationAdjustment ?? items.reduce((sum: number, item: any) => sum + (item.revaluationAdjustment ?? 0), 0),
      };
      setCurrentBatch(formattedBatch);
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <div className="text-muted-foreground">Loading batches...</div>
      </div>
    );
  }

  if (sortedBatches.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <div className="rounded-full bg-muted p-3 mb-4">
          <FileText className="h-6 w-6 text-muted-foreground" />
        </div>
        <h3 className="font-semibold text-lg mb-1">No transactions found</h3>
        <p className="text-sm text-muted-foreground">
          {searchQuery ? 'Try adjusting your search criteria' : 'Start a new revaluation batch to get started'}
        </p>
      </div>
    );
  }

  return (
    <div className="border rounded-lg overflow-hidden">
      <Table>
        <TableHeader className="sticky top-0 z-10 bg-slate-50 dark:bg-slate-900">
          <TableRow className="border-b-2 border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800">
            <TableHead className="h-12 font-semibold">
              <button
                onClick={() => onSort('reference')}
                className="flex items-center hover:bg-slate-200 dark:hover:bg-slate-700 px-2 py-1 rounded transition-colors"
              >
                Reference
                <SortIcon columnKey="reference" sortKey={sortConfig.key} sortDirection={sortConfig.direction} />
              </button>
            </TableHead>
            <TableHead className="h-12 font-semibold">
              <button
                onClick={() => onSort('date')}
                className="flex items-center hover:bg-slate-200 dark:hover:bg-slate-700 px-2 py-1 rounded transition-colors"
              >
                Date
                <SortIcon columnKey="date" sortKey={sortConfig.key} sortDirection={sortConfig.direction} />
              </button>
            </TableHead>
            <TableHead className="h-12 font-semibold">
              <button
                onClick={() => onSort('period')}
                className="flex items-center hover:bg-slate-200 dark:hover:bg-slate-700 px-2 py-1 rounded transition-colors"
              >
                Period
                <SortIcon columnKey="period" sortKey={sortConfig.key} sortDirection={sortConfig.direction} />
              </button>
            </TableHead>
            <TableHead className="h-12 font-semibold">
              <button
                onClick={() => onSort('year')}
                className="flex items-center hover:bg-slate-200 dark:hover:bg-slate-700 px-2 py-1 rounded transition-colors"
              >
                Year
                <SortIcon columnKey="year" sortKey={sortConfig.key} sortDirection={sortConfig.direction} />
              </button>
            </TableHead>
            <TableHead className="h-12 font-semibold">
              <button
                onClick={() => onSort('assets')}
                className="flex items-center hover:bg-slate-200 dark:hover:bg-slate-700 px-2 py-1 rounded transition-colors"
              >
                Assets
                <SortIcon columnKey="assets" sortKey={sortConfig.key} sortDirection={sortConfig.direction} />
              </button>
            </TableHead>
            <TableHead className="h-12 font-semibold text-right">
              <button
                onClick={() => onSort('adjustment')}
                className="flex items-center ml-auto hover:bg-slate-200 dark:hover:bg-slate-700 px-2 py-1 rounded transition-colors"
              >
                Adjustment
                <SortIcon columnKey="adjustment" sortKey={sortConfig.key} sortDirection={sortConfig.direction} />
              </button>
            </TableHead>
            <TableHead className="h-12 font-semibold">
              <button
                onClick={() => onSort('postedBy')}
                className="flex items-center hover:bg-slate-200 dark:hover:bg-slate-700 px-2 py-1 rounded transition-colors"
              >
                Posted By
                <SortIcon columnKey="postedBy" sortKey={sortConfig.key} sortDirection={sortConfig.direction} />
              </button>
            </TableHead>
            <TableHead className="h-12 font-semibold">
              <button
                onClick={() => onSort('postedDate')}
                className="flex items-center hover:bg-slate-200 dark:hover:bg-slate-700 px-2 py-1 rounded transition-colors"
              >
                Posted Date
                <SortIcon columnKey="postedDate" sortKey={sortConfig.key} sortDirection={sortConfig.direction} />
              </button>
            </TableHead>
            <TableHead className="h-12 font-semibold">
              <button
                onClick={() => onSort('status')}
                className="flex items-center hover:bg-slate-200 dark:hover:bg-slate-700 px-2 py-1 rounded transition-colors"
              >
                Status
                <SortIcon columnKey="status" sortKey={sortConfig.key} sortDirection={sortConfig.direction} />
              </button>
            </TableHead>
            <TableHead className="h-12 font-semibold text-right"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {paginatedBatches.map((batch, index) => (
            <TableRow
              key={batch.id}
              className={`
                border-b border-slate-100 dark:border-slate-800
                ${index % 2 === 0 ? 'bg-white dark:bg-slate-950' : 'bg-slate-50/50 dark:bg-slate-900/50'}
                hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors
              `}
              {...(index === 0 && { 'data-tour': 'revaluation-impact' })}
            >
              <TableCell className="font-medium">{batch.batchReference || batch.id}</TableCell>
              <TableCell>
                {batch.dateOfRun ? new Date(batch.dateOfRun).toLocaleDateString('en-GB') : batch.date || '-'}
              </TableCell>
              <TableCell>{batch.period ?? '-'}</TableCell>
              <TableCell>{batch.year ?? '-'}</TableCell>
              <TableCell>{batch.items?.length ?? batch.assets ?? 0}</TableCell>
              <TableCell className="text-right">
                <span className={batch.totalRevaluationAdjustment >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}>
                  {(batch.totalRevaluationAdjustment ?? batch.amount) >= 0 ? '+' : ''}
                  {formatCurrency(batch.totalRevaluationAdjustment ?? batch.amount ?? 0)}
                </span>
              </TableCell>
              <TableCell>{batch.postedBy || '-'}</TableCell>
              <TableCell>
                {batch.postedDate ? new Date(batch.postedDate).toLocaleDateString('en-GB') : '-'}
              </TableCell>
              <TableCell>
                <Badge
                  variant="secondary"
                  className={
                    batch.status === 'Completed'
                      ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                      : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                  }
                >
                  <CheckCircle2 className="h-3 w-3 mr-1" />
                  {batch.status}
                </Badge>
              </TableCell>
              <TableCell className="text-right">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleViewBatch(batch)}
                >
                  <Eye className="h-4 w-4 mr-2" />
                  View
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      
      {/* Pagination and Summary */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between gap-4 px-4 py-3 border-t bg-slate-50/50 dark:bg-slate-900/50">
          <div className="text-sm text-muted-foreground flex-shrink-0">
            Showing {startIndex + 1}-{Math.min(endIndex, sortedBatches.length)} of {sortedBatches.length} transactions
            {sortedBatches.length !== batches.length && ` (filtered from ${batches.length} total)`}
          </div>
          
          {/* Pagination Controls */}
          <div className="flex items-center justify-center gap-2 flex-shrink-0">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
            >
              First
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1}
            >
              <ChevronLeft className="h-4 w-4" />
              Previous
            </Button>
            
            {/* Page Number Buttons */}
            <div className="flex items-center gap-1">
              {(() => {
                const pageNumbers: (number | string)[] = [];
                const maxVisiblePages = 7;
                
                if (totalPages <= maxVisiblePages) {
                  // Show all pages if total is small
                  for (let i = 1; i <= totalPages; i++) {
                    pageNumbers.push(i);
                  }
                } else {
                  // Always show first page
                  pageNumbers.push(1);
                  
                  if (currentPage > 3) {
                    pageNumbers.push('...');
                  }
                  
                  // Show pages around current page
                  const start = Math.max(2, currentPage - 1);
                  const end = Math.min(totalPages - 1, currentPage + 1);
                  
                  for (let i = start; i <= end; i++) {
                    pageNumbers.push(i);
                  }
                  
                  if (currentPage < totalPages - 2) {
                    pageNumbers.push('...');
                  }
                  
                  // Always show last page
                  pageNumbers.push(totalPages);
                }
                
                return pageNumbers.map((page, pageIndex) => {
                  if (page === '...') {
                    return (
                      <span key={`ellipsis-before-${pageNumbers[pageIndex + 1]}`} className="px-2 text-muted-foreground">
                        ...
                      </span>
                    );
                  }
                  
                  return (
                    <Button
                      key={`page-${page}`}
                      variant={currentPage === page ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setCurrentPage(page as number)}
                      className="min-w-[36px]"
                    >
                      {page}
                    </Button>
                  );
                });
              })()}
            </div>
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
            >
              Last
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function RevaluationPageContent(): React.ReactElement {
  const location = useLocation();
  const navigate = useNavigate();
  const { isDemo, setMode } = useMode();
  
  // Product Tour
  const { isTourOpen, tourSteps, startTour, closeTour, completeTour } = useRevaluationTour();
  const { setOnStartTour } = useTour();
  
  // Register tour with context
  useEffect(() => {
    setOnStartTour(() => startTour);
    return () => setOnStartTour(null);
  }, [setOnStartTour, startTour]);
  
  const [showAssetSelection, setShowAssetSelection] = useState(false);
  const [currentBatch, setCurrentBatch] = useState<RevaluationBatch | null>(null);
  const [selectedItemIds, setSelectedItemIds] = useState<Set<string>>(new Set());
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showProcessConfirm, setShowProcessConfirm] = useState(false);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
    const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
    const [showModifiedHighlight, setShowModifiedHighlight] = useState(false);
    const [completedBatches, setCompletedBatches] = useState<any[]>(mockRevaluationBatches);
    const [isLoadingBatches, setIsLoadingBatches] = useState(false);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [originalBatch, setOriginalBatch] = useState<RevaluationBatch | null>(null);
    const [showComingSoon, setShowComingSoon] = useState(false);
  const [showDemoWarning, setShowDemoWarning] = useState(false);
  const [statistics, setStatistics] = useState<any>(null);
  const [isLoadingStatistics, setIsLoadingStatistics] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortConfig, setSortConfig] = useState<{ key: string | null; direction: 'asc' | 'desc' | null }>({
    key: null,
    direction: null,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const pageSize = 25;

  // Register tour with context
  useEffect(() => {
    setOnStartTour(() => startTour);
    return () => setOnStartTour(null);
  }, [setOnStartTour, startTour]);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoadingBatches(true);
      setIsLoadingStatistics(true);
      try {
        // Fetch batches and statistics in parallel
        const [batches, stats] = await Promise.all([
          dataService.getRevaluationBatches(),
          dataService.getRevaluationStatistics()
        ]);
        
        if (batches && batches.length > 0) {
          const sortedBatches = batches.sort((a: any, b: any) => 
            new Date(b.dateOfRun).getTime() - new Date(a.dateOfRun).getTime()
          );
          setCompletedBatches(sortedBatches);
        } else {
          setCompletedBatches(mockRevaluationBatches);
        }
        
        setStatistics(stats);
      } catch (error) {
        console.error('Error fetching revaluation data:', error);
        setCompletedBatches(mockRevaluationBatches);
        // Set default statistics on error - using extracted helper function (S3776)
        setStatistics(createDefaultStatistics());
      } finally {
        setIsLoadingBatches(false);
        setIsLoadingStatistics(false);
      }
    };

    if (!currentBatch) {
      fetchData();
    }
  }, [currentBatch, refreshTrigger]);

  // Handle selected assets passed from assets page and URL parameters
  useEffect(() => {
    const state = location.state as { selectedAssets?: Asset[]; refresh?: boolean; viewBatch?: boolean } | null;
    const searchParams = new URLSearchParams(location.search);
    const batchId = searchParams.get('batch');
    
    // Handle refresh from breadcrumb click (when clicking current page without batch param)
    if (state?.refresh && !batchId) {
      console.log('Soft reloading current page');
      // Clear the current batch and reset page state
      setCurrentBatch(null);
      setSearchQuery('');
      setSortConfig({ key: null, direction: null });
      setRefreshTrigger(prev => prev + 1); // Trigger data refetch
      // Clear the state to prevent re-triggering
      window.history.replaceState({}, document.title);
      return;
    }
    
    // Handle clearing batch when navigating back to main page (no batch param)
    if (!batchId && currentBatch) {
      setCurrentBatch(null);
      setRefreshTrigger(prev => prev + 1);
      return;
    }
    
    if (state?.selectedAssets && state.selectedAssets.length > 0) {
      handleAssetsSelected(state.selectedAssets);
      // Clear the state to prevent re-triggering on navigation
      window.history.replaceState({}, document.title);
    }
  }, [location.state, location.search]);

  // Reset pagination when search query changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  // Auto-recalculate items when header revaluation mode or amount changes
  useEffect(() => {
    if (!currentBatch || currentBatch.items.length === 0) return;
    
    // Recalculate only non-overridden items
    const updatedItems = currentBatch.items.map(item => {
      // Skip overridden items
      if (item.isOverridden) {
        return item;
      }

      const revaluedNBV = calculateRevaluedNBV(
        item.currentNBV,
        currentBatch.revaluationMode,
        currentBatch.revaluationAmount
      );
      const revaluationAdjustment = revaluedNBV - item.currentNBV;

      return {
        ...item,
        revaluedNBV,
        revaluationAdjustment,
        aiRevaluedNBV: revaluedNBV,
      };
    });

    // Only update if there are actual changes
    const hasChanges = updatedItems.some((item, index) => 
      item.revaluedNBV !== currentBatch.items[index].revaluedNBV ||
      item.revaluationAdjustment !== currentBatch.items[index].revaluationAdjustment
    );

    if (hasChanges) {
      const totalRevaluationAdjustment = updatedItems.reduce((sum, item) => sum + item.revaluationAdjustment, 0);
      const totalCurrentNBV = updatedItems.reduce((sum, item) => sum + item.currentNBV, 0);
      const totalRevaluedNBV = updatedItems.reduce((sum, item) => sum + item.revaluedNBV, 0);

      setCurrentBatch({
        ...currentBatch,
        items: updatedItems,
        totalRevaluationAdjustment,
        totalCurrentNBV,
        totalRevaluedNBV,
      });
    }
  }, [currentBatch?.revaluationMode, currentBatch?.revaluationAmount]);

  const handleStartRun = () => {
    if (isDemo) {
      setShowDemoWarning(true);
      return;
    }
    setShowAssetSelection(true);
  };

  const handleViewLastBatchSummary = async () => {
    if (completedBatches.length === 0) {
      toast.error('No batch history available', {
        description: 'There are no completed revaluation batches to view.',
      });
      return;
    }

    // Find the most recent batch (already sorted by date in useEffect)
    const lastBatch = completedBatches[0];
    
    // Navigate to the batch view URL
    navigate(`/transactions/revaluation?batch=${lastBatch.id}`, {
      state: { viewBatch: true }
    });
    
    try {
      // Load the batch details
      if (lastBatch.id && isServerGeneratedId(lastBatch.id)) {
        const fullBatch = await dataService.getRevaluationBatch(lastBatch.id);
        const items = fullBatch.items ?? [];
        const formattedBatch = {
          ...fullBatch,
          dateOfRun: fullBatch.dateOfRun?.split('T')[0] || fullBatch.dateOfRun,
          items,
          totalCurrentNBV: fullBatch.totalCurrentNBV ?? items.reduce((sum: number, item: any) => sum + (item.currentNBV ?? 0), 0),
          totalRevaluedNBV: fullBatch.totalRevaluedNBV ?? items.reduce((sum: number, item: any) => sum + (item.revaluedNBV ?? 0), 0),
          totalRevaluationAdjustment: fullBatch.totalRevaluationAdjustment ?? items.reduce((sum: number, item: any) => sum + (item.revaluationAdjustment ?? 0), 0),
        };
        setCurrentBatch(formattedBatch);
      } else {
        const items = lastBatch.items ?? [];
        const formattedBatch = {
          ...lastBatch,
          dateOfRun: lastBatch.dateOfRun?.split('T')[0] || lastBatch.dateOfRun,
          items,
          totalCurrentNBV: lastBatch.totalCurrentNBV ?? items.reduce((sum: number, item: any) => sum + (item.currentNBV ?? 0), 0),
          totalRevaluedNBV: lastBatch.totalRevaluedNBV ?? items.reduce((sum: number, item: any) => sum + (item.revaluedNBV ?? 0), 0),
          totalRevaluationAdjustment: lastBatch.totalRevaluationAdjustment ?? items.reduce((sum: number, item: any) => sum + (item.revaluationAdjustment ?? 0), 0),
        };
        setCurrentBatch(formattedBatch);
      }
      
      toast.success('Batch loaded', {
        description: `Viewing batch ${lastBatch.batchReference || lastBatch.id} in read-only mode.`,
      });
    } catch (error) {
      console.error('Error loading last batch:', error);
      // Fallback to loading the batch as-is
      const items = lastBatch.items ?? [];
      const formattedBatch = {
        ...lastBatch,
        dateOfRun: lastBatch.dateOfRun?.split('T')[0] || lastBatch.dateOfRun,
        items,
        totalCurrentNBV: lastBatch.totalCurrentNBV ?? items.reduce((sum: number, item: any) => sum + (item.currentNBV ?? 0), 0),
        totalRevaluedNBV: lastBatch.totalRevaluedNBV ?? items.reduce((sum: number, item: any) => sum + (item.revaluedNBV ?? 0), 0),
        totalRevaluationAdjustment: lastBatch.totalRevaluationAdjustment ?? items.reduce((sum: number, item: any) => sum + (item.revaluationAdjustment ?? 0), 0),
      };
      setCurrentBatch(formattedBatch);
      
      toast.warning('Batch loaded with limited data', {
        description: 'Some batch details may be unavailable.',
      });
    }
  };

  const handleAddMoreAssets = () => {
    setShowAssetSelection(true);
  };

  const handleRemoveSelectedItems = () => {
    if (!currentBatch || selectedItemIds.size === 0) return;
    setShowRemoveConfirm(true);
  };

  const confirmRemoveSelectedItems = () => {
    if (!currentBatch || selectedItemIds.size === 0) return;
    
    const updatedItems = currentBatch.items.filter(item => !selectedItemIds.has(item.id));
    
    const totalRevaluationAdjustment = updatedItems.reduce((sum, item) => sum + item.revaluationAdjustment, 0);
    const totalCurrentNBV = updatedItems.reduce((sum, item) => sum + item.currentNBV, 0);
    const totalRevaluedNBV = updatedItems.reduce((sum, item) => sum + item.revaluedNBV, 0);
    
    setCurrentBatch({
      ...currentBatch,
      items: updatedItems,
      totalRevaluationAdjustment,
      totalCurrentNBV,
      totalRevaluedNBV,
    });
    
    setSelectedItemIds(new Set());
    setShowRemoveConfirm(false);
  };

  const handleAssetsSelected = (selectedAssets: Asset[]) => {
    // Create revaluation items from selected assets - default to 0.00 until user enters revaluation amount
    const newItems: RevaluationItem[] = selectedAssets.map((asset, index) => {
      return {
        id: `rev-item-${Date.now()}-${index}`,
        assetId: asset.id,
        assetCode: asset.code,
        assetDescription: asset.name,
        category: asset.category,
        location: asset.location,
        currentNBV: asset.netBookValue,
        aiRevaluedNBV: 0, // Default to 0.00 until user enters revaluation amount
        confidence: 'High' as const,
        revaluedNBV: 0, // Default to 0.00 (no adjustment)
        revaluationAdjustment: 0, // Default to 0 until user enters revaluation amount
        validationStatus: 'valid',
      };
    });

    // If we have an existing batch, add to it; otherwise create new batch
    if (currentBatch) {
      // Filter out duplicates based on assetId
      const existingAssetIds = new Set(currentBatch.items.map(item => item.assetId));
      const uniqueNewItems = newItems.filter(item => !existingAssetIds.has(item.assetId));
      
      const allItems = [...currentBatch.items, ...uniqueNewItems];
      const totalRevaluationAdjustment = allItems.reduce((sum, item) => sum + item.revaluationAdjustment, 0);
      const totalCurrentNBV = allItems.reduce((sum, item) => sum + item.currentNBV, 0);
      const totalRevaluedNBV = allItems.reduce((sum, item) => sum + item.revaluedNBV, 0);
      
      setCurrentBatch({
        ...currentBatch,
        items: allItems,
        totalRevaluationAdjustment,
        totalCurrentNBV,
        totalRevaluedNBV,
      });
    } else {
      // Create new batch
      const totalRevaluationAdjustment = newItems.reduce((sum, item) => sum + item.revaluationAdjustment, 0);
      const totalCurrentNBV = newItems.reduce((sum, item) => sum + item.currentNBV, 0);
      const totalRevaluedNBV = newItems.reduce((sum, item) => sum + item.revaluedNBV, 0);

      const batch: RevaluationBatch = {
        id: `batch-${Date.now()}`,
        batchReference: '',
        dateOfRun: new Date().toISOString().split('T')[0],
        status: 'In Progress',
        notes: '',
        items: newItems,
        revaluationMode: 'ByPercentage',
        revaluationAmount: 0,
        createdBy: 'Current User',
        createdDate: new Date().toISOString(),
        modifiedBy: 'Current User',
        modifiedDate: new Date().toISOString(),
        preparedBy: 'Current User',
        preparedDate: new Date().toISOString(),
        currentStep: 'Prepare',
        totalRevaluationAdjustment,
        totalCurrentNBV,
        totalRevaluedNBV,
        currency: 'GBP',
      };

      setCurrentBatch(batch);
    }
    
    setShowAssetSelection(false);
  };

  const handleUpdateItem = (itemId: string, updates: Partial<RevaluationItem>) => {
    if (!currentBatch) return;

    const updatedItems = currentBatch.items.map(item =>
      item.id === itemId ? { ...item, ...updates } : item
    );

    const totalRevaluationAdjustment = updatedItems.reduce((sum, item) => sum + item.revaluationAdjustment, 0);
    const totalCurrentNBV = updatedItems.reduce((sum, item) => sum + item.currentNBV, 0);
    const totalRevaluedNBV = updatedItems.reduce((sum, item) => sum + item.revaluedNBV, 0);

    setCurrentBatch({
      ...currentBatch,
      items: updatedItems,
      totalRevaluationAdjustment,
      totalCurrentNBV,
      totalRevaluedNBV,
    });
  };

  const handleReapplyDefaults = (applyToAll: boolean) => {
    if (!currentBatch) return;

    const updatedItems = recalculateRevaluationItems(
      currentBatch.items,
      currentBatch.revaluationMode,
      currentBatch.revaluationAmount,
      applyToAll
    );

    const totalRevaluationAdjustment = updatedItems.reduce((sum, item) => sum + item.revaluationAdjustment, 0);
    const totalCurrentNBV = updatedItems.reduce((sum, item) => sum + item.currentNBV, 0);
    const totalRevaluedNBV = updatedItems.reduce((sum, item) => sum + item.revaluedNBV, 0);

    setCurrentBatch({
      ...currentBatch,
      items: updatedItems,
      totalRevaluationAdjustment,
      totalCurrentNBV,
      totalRevaluedNBV,
    });

    toast.success('Defaults reapplied', {
      description: applyToAll 
        ? 'All lines have been recalculated from header values.'
        : 'Unmodified lines have been recalculated from header values.',
    });
  };
  const isServerGeneratedId = (id: string) => {
    const guidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    return guidRegex.test(id);
  };

  const handleSort = (key: string) => {
    setSortConfig(prevConfig => {
      // If clicking the same column, cycle through: asc -> desc -> null
      if (prevConfig.key === key) {
        if (prevConfig.direction === 'asc') {
          return { key, direction: 'desc' };
        } else if (prevConfig.direction === 'desc') {
          return { key: null, direction: null };
        }
      }
      // If clicking a new column, start with asc
      return { key, direction: 'asc' };
    });
    setCurrentPage(1);
  };


  const handleSaveBatch = async () => {
    if (!currentBatch) return;
    
    try {
      const batchToSave = {
        ...currentBatch,
        status: currentBatch.status || 'In Progress',
      };
      
      if (currentBatch.id && isServerGeneratedId(currentBatch.id)) {
        const updatedBatch = await dataService.updateRevaluationBatch(currentBatch.id, batchToSave);
        setCurrentBatch({
          ...updatedBatch,
          dateOfRun: updatedBatch.dateOfRun?.split('T')[0] || updatedBatch.dateOfRun,
        });
      } else {
        const savedBatch = await dataService.createRevaluationBatch(batchToSave);
        setCurrentBatch({
          ...savedBatch,
          dateOfRun: savedBatch.dateOfRun?.split('T')[0] || savedBatch.dateOfRun,
        });
      }
      
      setShowModifiedHighlight(true);
      setTimeout(() => setShowModifiedHighlight(false), 2000);
      
      toast.success('Batch saved', { description: 'Revaluation batch saved successfully' });
    } catch (error) {
      console.error('Failed to save revaluation batch:', error);
      toast.error('Failed to save batch', { description: 'Please ensure you are in API mode and try again.' });
    }
  };

  const handleCancelBatch = () => {
    setShowCancelConfirm(true);
  };

  const confirmCancelBatch = () => {
    setCurrentBatch(null);
    setSelectedItemIds(new Set());
    setLastSavedAt(null);
    setShowCancelConfirm(false);
  };

  const handleProcessBatch = () => {
    if (!currentBatch) return;

    // Validate mandatory fields
    if (!currentBatch.batchReference || currentBatch.batchReference.trim() === '') {
      toast.error('Reference is required', {
        description: 'Please enter a reference before processing.',
      });
      return;
    }

    if (!currentBatch.dateOfRun || currentBatch.dateOfRun.trim() === '') {
      toast.error('Date is required', {
        description: 'Please select a date before processing.',
      });
      return;
    }

    setShowProcessConfirm(true);
  };

  const confirmProcessBatch = async () => {
    if (!currentBatch) return;
    
    const reference = currentBatch.batchReference;
    
    try {
      let batchId = currentBatch.id;
      
      // Ensure batch is saved first to get a server-generated ID
      if (!isServerGeneratedId(currentBatch.id)) {
        const savedBatch = await dataService.createRevaluationBatch(currentBatch);
        batchId = savedBatch.id;
      } else {
        // Update the batch to ensure latest changes are saved
        await dataService.updateRevaluationBatch(currentBatch.id, currentBatch);
      }
      
      // Call the backend PostBatch endpoint which creates transactions and posts to Financials
      const result = await dataService.postRevaluationBatch(batchId, 'Current User');
      
      // Check for posting failures
      const postingResult = result?.postingResult;
      const hasCreationFailures = postingResult?.transactionCreationFailures?.length > 0;
      const hasPostingFailures = postingResult?.financialsPostingFailures?.length > 0;
      
      if (hasCreationFailures || hasPostingFailures) {
        // Build failure message
        const failureMessages: string[] = [];
        const errorDetails: string[] = [];
        
        if (hasCreationFailures) {
          const count = postingResult.transactionCreationFailures.length;
          failureMessages.push(`${count} transaction(s) failed to create`);
          postingResult.transactionCreationFailures.forEach((failure: { assetCode: string; errorMessage: string }) => {
            console.error(`Transaction creation failed for asset ${failure.assetCode}: ${failure.errorMessage}`);
            errorDetails.push(`Asset '${failure.assetCode}': ${formatApiErrorMessage(failure.errorMessage)}`);
          });
        }
        
        if (hasPostingFailures) {
          const count = postingResult.financialsPostingFailures.length;
          failureMessages.push(`${count} transaction(s) failed to post to Financials`);
          postingResult.financialsPostingFailures.forEach((failure: { assetCode: string; errorMessage: string }) => {
            console.error(`Financials posting failed for asset ${failure.assetCode}: ${failure.errorMessage}`);
            errorDetails.push(`Asset '${failure.assetCode}': ${formatApiErrorMessage(failure.errorMessage)}`);
          });
        }
        
        // Build description with error details (show first 3 errors, then indicate if more)
        const maxErrorsToShow = 3;
        const displayedErrors = errorDetails.slice(0, maxErrorsToShow);
        const remainingCount = errorDetails.length - maxErrorsToShow;
        let description = `Reference: ${reference}. ${failureMessages.join('. ')}.\n${displayedErrors.join('\n')}`;
        if (remainingCount > 0) {
          description += `\n...and ${remainingCount} more error(s)`;
        }
        
        // Show warning toast with failure details
        toast.warning('Revaluation Completed with Warnings', {
          description,
          duration: 10000,
        });
      }else {
        // Show success toast
        toast.success('Revaluation Created Successfully', {
          description: `Reference: ${reference}`,
        });
      }
      
      // Clear batch and navigate back to processing screen
      setCurrentBatch(null);
      setSelectedItemIds(new Set());
      setShowProcessConfirm(false);
      
      // Navigate back to processing screen
      navigate('/transactions/revaluation');
    } catch (error) {
      console.error('Failed to process revaluation batch:', error);
      toast.error('Failed to process revaluation batch', {
        description: 'Please ensure you are in API mode and try again.',
      });
      setShowProcessConfirm(false);
    }
  };

  // Landing page view (no active batch)
  if (!currentBatch) {
    return (
      <div className="flex h-full flex-col">
        {/* Page Header */}
        <div className="border-b bg-card p-6" data-tour="revaluation-header">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
                <TrendingUp className="h-6 w-6 text-primary" />
                Revaluations
              </h1>
              <p className="text-sm text-muted-foreground mt-1">View, create, or edit transactions that adjust asset net book value.</p>
            </div>
                    <div className="flex items-center gap-2">
                      <EvoFeatureFeedbackCollector feature="Revaluation Transactions" />
                      <Button variant="outline" onClick={handleViewLastBatchSummary} data-tour="view-summary-button">
                        <Eye className="mr-2 h-4 w-4" />
                        View Last Batch Summary
                      </Button>
                      <Button onClick={handleStartRun} data-tour="start-batch-button">
                        <Play className="mr-2 h-4 w-4" />
                        Start New Revaluation Batch
                      </Button>
                    </div>
                    </div>
                  </div>

                {/* Content */}
        <div className="flex-1 overflow-auto">
          <div className="space-y-6 p-6">
        <div className="grid gap-4 md:grid-cols-3" data-tour="revaluation-cards">
          {isLoadingStatistics ? (
            <>
              <Card className="overflow-hidden border-0">
                <div className="relative h-24 p-4 bg-teal-100">
                  <div className="flex items-center justify-center h-full">
                    <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                  </div>
                </div>
                <div className="p-4 bg-white">
                  <div className="h-4 bg-muted rounded w-1/2"></div>
                </div>
              </Card>
              <Card className="overflow-hidden border-0">
                <div className="relative h-24 p-4 bg-success-100">
                  <div className="flex items-center justify-center h-full">
                    <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                  </div>
                </div>
                <div className="p-4 bg-white">
                  <div className="h-4 bg-muted rounded w-1/2"></div>
                </div>
              </Card>
              <Card className="overflow-hidden border-0">
                <div className="relative h-24 p-4 bg-warning-100">
                  <div className="flex items-center justify-center h-full">
                    <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                  </div>
                </div>
                <div className="p-4 bg-white">
                  <div className="h-4 bg-muted rounded w-1/2"></div>
                </div>
              </Card>
            </>
          ) : (
            <>
              <StaticMetricCardAlt
                title="Net Revaluation Adjustments"
                value={`${(statistics?.totalRevaluationAdjustment ?? 0) >= 0 ? '+' : ''}£${(statistics?.totalRevaluationAdjustment ?? 0).toLocaleString('en-GB', { minimumFractionDigits: 2 })}`}
                change={`This year (${statistics?.year || new Date().getFullYear()})`}
                changeLabel=""
                icon={TrendingUp}
                variant="info"
              />

              <StaticMetricCardAlt
                title="Assets Revalued"
                value={(statistics?.totalAssetsRevalued ?? 0).toString()}
                change={`Across ${statistics?.totalBatches ?? 0} revaluation`}
                changeLabel={(statistics?.totalBatches ?? 0) === 1 ? 'batch' : 'batches'}
                icon={RefreshCw}
                variant="info"
              />

              <StaticMetricCardAlt
                title="Pending Approval"
                value={(statistics?.pendingBatchCount ?? 0).toString()}
                change={`${(statistics?.pendingAdjustment ?? 0) >= 0 ? '+' : ''}£${(statistics?.pendingAdjustment ?? 0).toLocaleString('en-GB', { minimumFractionDigits: 2 })}`}
                changeLabel="adjustment"
                icon={Clock}
                variant="info"
              />
            </>
          )}
        </div>

        {/* Recent Batches */}
        <div>
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl font-semibold">Transaction History</h2>
              <p className="text-sm text-muted-foreground mt-1">View and manage all revaluation transactions</p>
            </div>
            <div className="relative w-80">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search transactions..."
                value={searchQuery}
                onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setSearchQuery(e.target.value)}
                className="pl-9 bg-white dark:bg-slate-900"
                data-tour="search-transactions"
              />
            </div>
          </div>
          
          <div data-tour="transaction-history">
            <TransactionHistoryList
              isLoading={isLoadingBatches}
              batches={completedBatches}
              searchQuery={searchQuery}
              isServerGeneratedId={isServerGeneratedId}
              setCurrentBatch={setCurrentBatch}
              sortConfig={sortConfig}
              onSort={handleSort}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              pageSize={pageSize}
            />
          </div>
        </div>
        </div>

          <AssetSelectionDialog
            open={showAssetSelection}
            onOpenChange={setShowAssetSelection}
            onConfirm={handleAssetsSelected}
            title="Select Assets for Revaluation"
            description="Choose one or more assets to add to this revaluation batch."
            excludeAssetIds={[]}
          />

          <ComingSoonModal
            open={showComingSoon}
            onOpenChange={setShowComingSoon}
            title="Batch Summary Coming Soon"
            description="The ability to view the last batch summary will be available in an upcoming release."
          />

          {/* Product Tour */}
          <ProductTour
            steps={tourSteps}
            isOpen={isTourOpen}
            onClose={closeTour}
            onComplete={completeTour}
          />
        </div>
      </div>
    );
  }

  // Batch processing view
  const readonly = currentBatch.status === 'Completed';

  return (
    <div className="relative flex flex-col h-full">
      <div className="flex-1 overflow-auto pb-32">
        {/* Page Header */}
        <div className="bg-card p-6 pb-0">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
                <RefreshCw className="h-6 w-6 text-primary" />
                Revaluations
              </h1>
              <p className="text-sm text-muted-foreground mt-1">
                Last Modified{' '}
                <span 
                  className={`font-semibold transition-colors duration-500 ${
                    showModifiedHighlight ? 'text-green-600 dark:text-green-400' : 'text-foreground'
                  }`}
                >
                  {currentBatch.modifiedDate ? new Date(currentBatch.modifiedDate).toLocaleString('en-GB', { 
                    weekday: 'long',
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  }).replace(/(\d+)/, (match) => {
                    const day = parseInt(match);
                    const suffix = day === 1 || day === 21 || day === 31 ? 'st' : 
                                   day === 2 || day === 22 ? 'nd' : 
                                   day === 3 || day === 23 ? 'rd' : 'th';
                    return `${day}${suffix}`;
                  }) : 'Never'}
                </span>
              </p>
            </div>
            <Badge
              variant="secondary"
              className={`text-base px-3 py-1 ${
                currentBatch.status === 'In Progress'
                  ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                  : 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
              }`}
            >
              {currentBatch.status === 'Completed' && <CheckCircle2 className="h-3 w-3 mr-1" />}
              {currentBatch.status === 'In Progress' && <Clock className="h-3 w-3 mr-1" />}
              {currentBatch.status}
            </Badge>
          </div>
          
          <div className="-mx-6 px-6 pt-6 pb-4 border-y border-border bg-[#F9FAFB] dark:bg-[#121212]">
            <div className="flex gap-4">
              {/* Left Column: Reference and Revalue */}
              <div className="space-y-2 flex-shrink-0" style={{ width: '300px' }}>
                <div className="space-y-2">
                  <Label htmlFor="batch-ref" className="font-semibold">
                    Reference <span className="text-red-500">*</span>
                  </Label>
                  <Input 
                    id="batch-ref" 
                    value={currentBatch.batchReference} 
                    onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setCurrentBatch({ ...currentBatch, batchReference: e.target.value })}
                    readOnly={readonly}
                  />
                </div>

                <div className="space-y-2" data-tour="revaluation-mode">
                  <div className="flex items-center gap-2">
                    <Label htmlFor="revalue-mode" className="font-semibold">
                      Revalue <span className="text-red-500">*</span>
                    </Label>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <HelpCircle className="h-4 w-4 text-muted-foreground cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent side="right" className="max-w-xs">
                          <div className="space-y-2 text-sm">
                            <p><strong>By Percentage</strong> — apply an uplift/downlift by a percentage of current NBV</p>
                            <p><strong>By Value</strong> — add/subtract a fixed amount to current NBV</p>
                            <p><strong>To Value</strong> — set NBV to an exact target value</p>
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                  <Select
                    value={currentBatch.revaluationMode}
                    onValueChange={(value: RevaluationMode) => {
                      if (readonly) return;
                      
                      // Check if there are any overridden items
                      const hasOverrides = currentBatch.items.some(item => item.isOverridden);
                      
                      if (hasOverrides && currentBatch.items.length > 0) {
                        toast.info('Defaults recalculated from header', {
                          description: "Use 'Reapply Defaults' to update overridden lines.",
                        });
                      }
                      
                      setCurrentBatch({ ...currentBatch, revaluationMode: value });
                    }}
                    disabled={readonly}
                  >
                    <SelectTrigger id="revalue-mode" className="bg-white dark:bg-black">
                      <SelectValue placeholder="Select revaluation mode" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ByPercentage">By Percentage</SelectItem>
                      <SelectItem value="ByValue">By Value</SelectItem>
                      <SelectItem value="ToValue">To Value</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Middle Column: Date and Revaluation Amount */}
              <div className="space-y-2 flex-shrink-0" style={{ width: '300px' }}>
                <div className="space-y-2">
                  <Label htmlFor="date-of-run" className="font-semibold">
                    Date <span className="text-red-500">*</span>
                  </Label>
                  <Input 
                    id="date-of-run" 
                    type="date" 
                    value={currentBatch.dateOfRun} 
                    onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setCurrentBatch({ ...currentBatch, dateOfRun: e.target.value })}
                    readOnly={readonly}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="revaluation-amount" className="font-semibold">
                    Revaluation Amount <span className="text-red-500">*</span>
                  </Label>
                  <Input 
                    id="revaluation-amount" 
                    type="number"
                    step={currentBatch.revaluationMode === 'ByPercentage' ? '0.0001' : '0.01'}
                    value={currentBatch.revaluationAmount}
                    onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                      if (readonly) return;
                      
                      // Allow empty string or parse the value (including negatives)
                      const value = e.target.value === '' ? 0 : parseFloat(e.target.value);
                      
                      // Only validate if we have a valid number
                      if (!isNaN(value)) {
                        // Validate the amount
                        const validation = validateRevaluationAmount(value, currentBatch.revaluationMode);
                        if (!validation.isValid) {
                          toast.error('Invalid amount', { description: validation.error });
                          return;
                        }
                        
                        setCurrentBatch({ ...currentBatch, revaluationAmount: value });
                      }
                    }}
                    placeholder={getRevaluationAmountPlaceholder(currentBatch.revaluationMode)}
                    readOnly={readonly}
                  />
                </div>
              </div>

              {/* Right Column: Notes spanning full height */}
              <div className="flex-1 flex flex-col space-y-2">
                <Label htmlFor="notes" className="font-semibold">Notes</Label>
                <Textarea
                  id="notes"
                  value={currentBatch.notes}
                  onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setCurrentBatch({ ...currentBatch, notes: e.target.value })}
                  placeholder="Add notes about this revaluation batch..."
                  className="flex-1 resize-none min-h-[100px] bg-white dark:bg-gray-950"
                  readOnly={readonly}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Detail Grid Header */}
        <div className="bg-card px-6 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-xl font-semibold">Revaluation Details</h3>
              <p className="text-sm text-muted-foreground mt-1">
                {currentBatch.items.length} {currentBatch.items.length === 1 ? 'asset' : 'assets'} added to this batch
              </p>
            </div>
            {!readonly && (
              <div className="flex items-center gap-3">
                <Button variant="default" className="h-[42px]" onClick={handleAddMoreAssets}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Assets
                </Button>
                {selectedItemIds.size > 0 && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="sm">
                        <MoreVertical className="h-4 w-4 mr-2" />
                        Actions ({selectedItemIds.size})
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={handleRemoveSelectedItems} className="text-destructive">
                        <Trash2 className="h-4 w-4 mr-2" />
                        Remove Selected
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
            )}
          </div>
        </div>
        
        {/* Table Container - Full height with no padding */}
        <RevaluationBatchGrid
          items={currentBatch.items}
          onUpdateItem={handleUpdateItem}
          onReapplyDefaults={handleReapplyDefaults}
          selectedItemIds={selectedItemIds}
          onSelectionChange={setSelectedItemIds}
          readonly={readonly}
          revaluationMode={currentBatch.revaluationMode}
          revaluationAmount={currentBatch.revaluationAmount}
        />
      </div>

      {/* Sticky Footer - Pinned to bottom */}
      <div className="absolute bottom-0 left-0 right-0 bg-background border-t shadow-lg">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Left: Action Buttons */}
            <div className="flex gap-2">
              {currentBatch.status === 'In Progress' && (
                <>
                  <Button variant="outline" onClick={handleCancelBatch}>
                    <X className="h-4 w-4 mr-2" />
                    Cancel
                  </Button>
                  <Button variant="outline" onClick={handleSaveBatch}>
                    <Save className="h-4 w-4 mr-2" />
                    Save
                  </Button>
                  <Button onClick={handleProcessBatch}>
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                    Save & Process
                  </Button>
                </>
              )}
              {currentBatch.status === 'Completed' && (
                <>
                  <Button variant="outline" onClick={() => setCurrentBatch(null)}>
                    <X className="h-4 w-4 mr-2" />
                    Back to Dashboard
                  </Button>
                </>
              )}
            </div>

            {/* Center: Spacer */}
            <div className="flex-1" />

            {/* Right: Summary Totals */}
            <div className="flex gap-8">
              <div className="text-right">
                <div className="text-xs text-muted-foreground">Current NBV</div>
                <div className="text-lg font-bold">
                  £{currentBatch.totalCurrentNBV.toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                </div>
              </div>
              <div className="text-right">
                <div className="text-xs text-muted-foreground">Revalued NBV</div>
                <div className="text-lg font-bold">
                  £{currentBatch.totalRevaluedNBV.toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                </div>
              </div>
              <div className="text-right">
                <div className="text-xs text-muted-foreground">Adjustment</div>
                <div className={`text-lg font-bold ${currentBatch.totalRevaluationAdjustment >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {currentBatch.totalRevaluationAdjustment >= 0 ? '+' : ''}£{currentBatch.totalRevaluationAdjustment.toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      <AssetSelectionDialog
        open={showAssetSelection}
        onOpenChange={setShowAssetSelection}
        onConfirm={handleAssetsSelected}
        title="Select Assets for Revaluation"
        description="Choose one or more assets to add to this revaluation batch."
        excludeAssetIds={currentBatch?.items.map(item => item.assetId) ?? []}
      />

      <ConfirmationModal
        open={showCancelConfirm}
        onOpenChange={setShowCancelConfirm}
        onConfirm={confirmCancelBatch}
        title="Cancel Revaluation Batch"
        description="Are you sure you want to cancel this revaluation batch? All unsaved changes will be lost."
        confirmText="Cancel Batch"
        type="danger"
      />

      <ConfirmationModal
        open={showProcessConfirm}
        onOpenChange={setShowProcessConfirm}
        onConfirm={confirmProcessBatch}
        title="Process Revaluation Batch"
        description="Are you sure you want to process this revaluation batch? This will update the asset values in the system."
        confirmText="Process Batch"
      />

      <ConfirmationModal
        open={showRemoveConfirm}
        onOpenChange={setShowRemoveConfirm}
        onConfirm={confirmRemoveSelectedItems}
        title="Remove Selected Assets"
        description={`Are you sure you want to remove ${selectedItemIds.size} selected asset${selectedItemIds.size !== 1 ? 's' : ''} from this batch?`}
        confirmText="Remove Assets"
        type="danger"
      />

      <DemoModeWarningModal
        open={showDemoWarning}
        onOpenChange={setShowDemoWarning}
        onSwitchToLive={() => setMode('live')}
      />

      {/* Product Tour */}
      <ProductTour
        steps={tourSteps}
        isOpen={isTourOpen}
        onClose={closeTour}
        onComplete={completeTour}
      />
    </div>
  );
}

export default function RevaluationPage(): React.ReactElement {
  return (
    <FeatureSettingsGuard>
      <RevaluationPageContent />
    </FeatureSettingsGuard>
  );
}
